'use client';
import { useEffect } from 'react';

import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import ServiceCard from './components/ServiceCard';
import CaseStudyCard from './components/CaseStudyCard';
import StatCard from './components/StatCard';

export default function Homepage() {
  // Scroll reveal animation
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  // Services data
  const services = [
  {
    id: 'service_1',
    icon: 'FilmIcon',
    title: 'Production',
    description: 'Full-scale video production, from concept to delivery. Commercials, branded content, and cinematic storytelling.'
  },
  {
    id: 'service_2',
    icon: 'PaintBrushIcon',
    title: 'Creative Direction',
    description: 'Strategic creative development that cuts through the noise. Brand identity, campaigns, and art direction.'
  },
  {
    id: 'service_3',
    icon: 'MegaphoneIcon',
    title: 'Marketing',
    description: 'Data-driven marketing strategies that deliver results. Digital campaigns, social media, and growth hacking.'
  },
  {
    id: 'service_4',
    icon: 'CpuChipIcon',
    title: 'Technology',
    description: 'Cutting-edge tech solutions for modern brands. Web development, apps, and interactive experiences.'
  }];


  // Case studies data
  const caseStudies = [
  {
    id: 'case_1',
    title: 'Vegas Nights Campaign',
    category: 'Hospitality',
    image: "https://images.unsplash.com/photo-1628840803162-6824ada8c7b0",
    imageAlt: 'Illuminated Las Vegas strip at night with casino lights and bustling crowds',
    slug: 'vegas-nights-campaign'
  },
  {
    id: 'case_2',
    title: 'High Roller Launch',
    category: 'Entertainment',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d2ac1bbe-1770508782200.png",
    imageAlt: 'Luxury casino interior with chandeliers and gaming tables',
    slug: 'high-roller-launch'
  },
  {
    id: 'case_3',
    title: 'Desert Dreams',
    category: 'Real Estate',
    image: "https://images.unsplash.com/photo-1671578731944-ac1c7dcf29d9",
    imageAlt: 'Modern luxury home exterior with desert landscape at sunset',
    slug: 'desert-dreams'
  },
  {
    id: 'case_4',
    title: 'Neon Rebrand',
    category: 'Branding',
    image: "https://images.unsplash.com/photo-1498088287194-16dea3614360",
    imageAlt: 'Vibrant neon signs and lights in downtown Las Vegas',
    slug: 'neon-rebrand'
  },
  {
    id: 'case_5',
    title: 'Strip Spectacular',
    category: 'Events',
    image: "https://images.unsplash.com/photo-1528130529020-8d5816f8a271",
    imageAlt: 'Large-scale event production with stage lights and crowd',
    slug: 'strip-spectacular'
  },
  {
    id: 'case_6',
    title: 'Golden Hour',
    category: 'Fashion',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_136efd5d3-1767277276578.png",
    imageAlt: 'Fashion photoshoot in desert setting during golden hour',
    slug: 'golden-hour'
  }];


  // Stats data
  const stats = [
  { id: 'stat_1', number: '200+', label: 'Projects Delivered' },
  { id: 'stat_2', number: '25', label: 'Years Experience' },
  { id: 'stat_3', number: '50+', label: 'Team Members' },
  { id: 'stat_4', number: '100%', label: 'Vegas Based' }];


  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground">
        {/* Hero Section */}
        <section className="min-h-screen flex items-center justify-center pt-24 pb-16 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto w-full">
            <div className="reveal">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary mb-8 block font-medium">
                Las Vegas, Nevada — Est. 2000
              </span>
              <h1 className="text-[18vw] sm:text-[16vw] md:text-hero leading-[0.85] font-bold tracking-tighter mb-12">
                BEST ODDS<br />
                <span className="text-primary neon-glow">CORP</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-12 leading-relaxed">
                Premium agency delivering high-stakes creative and production work 
                for the world's most ambitious brands. Based in Vegas. Built for performance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  href="/case-studies"
                  className="px-8 py-4 bg-primary text-primary-foreground text-xs uppercase tracking-widest hover:bg-primary/90 transition-colors font-semibold text-center border-glow">
                  
                  View Our Work
                </Link>
                <Link
                  href="/book"
                  className="px-8 py-4 border border-border text-foreground text-xs uppercase tracking-widest hover:border-primary hover:text-primary transition-colors font-semibold text-center">
                  
                  Book Consultation
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="mb-16 reveal">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary mb-4 block font-medium">
                01 — What We Do
              </span>
              <h2 className="text-5xl md:text-6xl font-bold tracking-tight">
                Core Services
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services?.map((service, index) =>
              <ServiceCard
                key={service?.id}
                icon={service?.icon}
                title={service?.title}
                description={service?.description}
                index={index} />

              )}
            </div>
          </div>
        </section>

        {/* Case Studies Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-8">
              <div className="reveal">
                <span className="text-[10px] uppercase tracking-ultra-wide text-primary mb-4 block font-medium">
                  02 — Featured Work
                </span>
                <h2 className="text-5xl md:text-6xl font-bold tracking-tight">
                  Case Studies
                </h2>
              </div>
              <Link
                href="/case-studies"
                className="reveal text-xs uppercase tracking-widest text-muted-foreground border-b border-border hover:text-primary hover:border-primary transition-colors pb-1">
                
                View All Projects
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {caseStudies?.map((caseStudy) =>
              <CaseStudyCard
                key={caseStudy?.id}
                id={caseStudy?.id}
                title={caseStudy?.title}
                category={caseStudy?.category}
                image={caseStudy?.image}
                imageAlt={caseStudy?.imageAlt}
                slug={caseStudy?.slug} />

              )}
            </div>
          </div>
        </section>

        {/* Credentials Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="reveal">
                <span className="text-[10px] uppercase tracking-ultra-wide text-primary mb-4 block font-medium">
                  03 — Agency Credentials
                </span>
                <h2 className="text-5xl md:text-6xl font-bold tracking-tight mb-8">
                  Proven Track<br />Record
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-12">
                  Since 2000, we've been pushing creative boundaries and delivering 
                  results for brands that demand excellence. Based in Las Vegas, 
                  we bring the energy and precision of the Strip to every project.
                </p>
                <div className="space-y-6">
                  <div className="flex items-start gap-4 reveal">
                    <div className="w-2 h-2 bg-primary mt-2 shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-widest mb-1">
                        Vegas Born
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Headquartered in the entertainment capital of the world
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 reveal">
                    <div className="w-2 h-2 bg-primary mt-2 shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-widest mb-1">
                        Award Winning
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Recognized industry-wide for creative excellence
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 reveal">
                    <div className="w-2 h-2 bg-primary mt-2 shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-widest mb-1">
                        Full Service
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        End-to-end capabilities from strategy to execution
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {stats?.map((stat, index) =>
                <StatCard
                  key={stat?.id}
                  number={stat?.number}
                  label={stat?.label}
                  index={index} />

                )}
              </div>
            </div>
          </div>
        </section>

        {/* Founder Trust Module */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-4">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary font-medium">
                04 — Founder-Led Execution
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-border">
              {/* Left: Headshot */}
              <div className="relative overflow-hidden bg-surface-1 min-h-[480px] lg:min-h-0">
                <img
                  src="/assets/images/Untitled-1771493284658.jpeg?v=2"
                  alt="Daniel Moravec — Founder of Best Odds Corp"
                  className="w-full h-full object-cover object-top absolute inset-0"
                />
                {/* Subtle overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
                {/* Name badge bottom-left */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-[10px] uppercase tracking-ultra-wide text-primary font-medium mb-1">Founder &amp; Operator</p>
                  <p className="text-xl font-bold text-white">Daniel Moravec</p>
                </div>
              </div>

              {/* Right: Content */}
              <div className="p-10 lg:p-14 flex flex-col justify-between bg-card">
                <div>
                  <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-tight">
                    You Work Directly<br />
                    <span className="text-primary">With the Operator.</span>
                  </h2>
                  <p className="text-base text-gray-300 leading-relaxed mb-8">
                    Best Odds Corp is founder-led. Daniel Moravec has spent 20+ years building, testing, and repositioning businesses — and now brings that operator judgment directly to client engagements. No account managers. No hand-offs. Just execution.
                  </p>

                  {/* Credibility Bullets */}
                  <ul className="space-y-3 mb-10">
                    {[
                      '20+ years building and operating businesses across industries',
                      'B.S. Marketing & Business Communication — The College of St. Scholastica',
                      'AI-fluent: builds apps, automations, and tools using AI as leverage',
                      'Active trader — decision-making under uncertainty is a core skill',
                      '135,658+ Afterhour followers in finance & trading',
                      'Sprint-based model: weekly execution, transparent reporting',
                    ]?.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 shrink-0" />
                        {bullet}
                      </li>
                    ))}
                  </ul>

                  {/* Social / Profile Trust Signals */}
                  <div className="flex flex-wrap items-center gap-4 mb-10 pb-10 border-b border-border">
                    <span className="text-[10px] uppercase tracking-ultra-wide text-muted-foreground">Find Daniel on</span>
                    <a
                      href="https://afterhour.com/adioking"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs uppercase tracking-widest text-foreground border border-border px-4 py-2 hover:border-primary hover:text-primary transition-colors font-medium"
                    >
                      Afterhour — @adioking
                    </a>
                    <a
                      href="https://x.com/adioking"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs uppercase tracking-widest text-foreground border border-border px-4 py-2 hover:border-primary hover:text-primary transition-colors font-medium"
                    >
                      X — @adioking
                    </a>
                  </div>
                </div>

                {/* CTA Bridge to Sprint */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    href="/work-with-daniel"
                    className="px-8 py-4 bg-primary text-primary-foreground text-xs uppercase tracking-widest hover:bg-primary/90 transition-colors font-semibold text-center border-glow"
                  >
                    Work With Daniel
                  </Link>
                  <Link
                    href="/team"
                    className="px-8 py-4 border border-border text-foreground text-xs uppercase tracking-widest hover:border-primary hover:text-primary transition-colors font-semibold text-center"
                  >
                    Full Founder Profile
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA Section */}
        <section className="py-32 px-6 border-b border-border">
          <div className="max-w-4xl mx-auto text-center">
            <div className="reveal">
              <span className="text-[10px] uppercase tracking-ultra-wide text-primary mb-8 block font-medium">
                05 — Let's Work Together
              </span>
              <h2 className="text-5xl md:text-7xl font-bold tracking-tight mb-8">
                Ready to<br />
                <span className="text-primary neon-glow">Make Moves?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed">
                Whether you're launching a new brand or scaling an existing one, 
                we're here to help you win. Let's talk strategy.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href="/book"
                  className="px-12 py-5 bg-primary text-primary-foreground text-sm uppercase tracking-widest hover:bg-primary/90 transition-colors font-bold border-glow">
                  
                  Book Consultation
                </Link>
                <Link
                  href="/contact"
                  className="px-12 py-5 border border-border text-foreground text-sm uppercase tracking-widest hover:border-primary hover:text-primary transition-colors font-bold">
                  
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );


}